'use client';

import type { NormalizedTrade } from '@/lib/hyperliquid/types';

interface TradeHistoryProps {
  symbol: string;
  basePrice: number;
  trades?: NormalizedTrade[];
  isLive?: boolean;
}

export default function TradeHistory({ symbol, basePrice: _basePrice, trades = [], isLive = false }: TradeHistoryProps) {
  const base = symbol.split('/')[0];

  return (
    <div className="overflow-hidden border-t border-[#2a2a2a]" style={{ background: '#141414' }}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#2a2a2a]" style={{ background: '#1e1e1e' }}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#F1F5F9]">Trade History</span>
          {isLive && (
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-[#22C55E] animate-pulse" />
              <span className="text-[9px] text-[#22C55E] font-medium uppercase tracking-wider">Live</span>
            </span>
          )}
        </div>
        <span className="text-xs text-[#94A3B8]">{symbol}</span>
      </div>

      {trades.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <div className="flex flex-col items-center gap-2">
            <div className="w-5 h-5 border-2 border-[#4c6ef4] border-t-transparent animate-spin" />
            <span className="text-xs text-[#94A3B8]">Connecting to live trades...</span>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-[#1e1e1e]">
              <tr className="border-b border-[#2a2a2a]">
                <th className="px-4 py-2 text-left text-[10px] text-[#94A3B8] uppercase tracking-wider font-medium">Time</th>
                <th className="px-4 py-2 text-left text-[10px] text-[#94A3B8] uppercase tracking-wider font-medium">Side</th>
                <th className="px-4 py-2 text-right text-[10px] text-[#94A3B8] uppercase tracking-wider font-medium">Price (USDC)</th>
                <th className="px-4 py-2 text-right text-[10px] text-[#94A3B8] uppercase tracking-wider font-medium">Size ({base})</th>
                <th className="px-4 py-2 text-right text-[10px] text-[#94A3B8] uppercase tracking-wider font-medium">Value (USDC)</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((trade, i) => (
                <tr
                  key={trade.id}
                  className={`border-b border-[#2a2a2a] transition-colors ${i === 0 ? 'animate-pulse' : ''}`}
                  style={{ background: '#242424', animationDuration: i === 0 ? '0.5s' : undefined, animationIterationCount: i === 0 ? '1' : undefined }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(76,110,244,0.06)')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#242424')}
                >
                  <td className="px-4 py-1.5 text-xs font-mono text-[#94A3B8] tabular-nums">{trade.time}</td>
                  <td className="px-4 py-1.5">
                    <span className={`text-xs font-semibold ${trade.side === 'Buy' ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                      {trade.side}
                    </span>
                  </td>
                  <td className={`px-4 py-1.5 text-xs font-mono tabular-nums text-right ${trade.side === 'Buy' ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                    {trade.price}
                  </td>
                  <td className="px-4 py-1.5 text-xs font-mono text-[#F1F5F9] tabular-nums text-right">{trade.size}</td>
                  <td className="px-4 py-1.5 text-xs font-mono text-[#94A3B8] tabular-nums text-right">
                    {parseFloat(trade.value).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}